import {DataFetcher, PriorityConfig} from '@aboutyou/data-fetcher';
import {useTranslation} from '@aboutyou/i18n';
import {
  ErrorPageInline,
  mediaDesktopAndUp,
  mediaLargeScreens,
} from '@aboutyou/ui-components';
import React, {memo} from 'react';
import styled from 'styled-components';
import {useActivatePreselectedBestCouponABTest} from '../../../features/Coupons/useActivatePreselectedBestCouponABTest';
import {ProductTileProps} from '../../../features/ProductTile/ProductTile';
import {
  ShallowRenderingState,
  useShallowRenderingState,
} from '../../../features/ProductTile/utils';
import {
  DeleteItemProvider,
  useWishlistPageStream,
} from '../hooks/useWishlistPageStream';
import {WishlistAddToBasketPayload} from '../WishlistProductTile/WishlistProductTile';
import {WishlistLoadingComponent} from './WishlistLoadingComponent';
import {WishlistPageStreamView} from './WishlistPageStreamView';
import {WishlistStreamView} from './WishlistStreamView';

const WishlistStreamViewMemo = memo(WishlistStreamView);
const WishlistPageStreamViewMemo = memo(WishlistPageStreamView);

const HISTORY_STATE_LAST_VISIBLE_ITEMS_KEY =
  'wishlist_product_stream_last_visible_items';

const WishlistItemStream = ({
  showConfirmOnDeleteItem,
  onClickAddToBasket,
  onClickSoldoutCta,
  gridColumns,
  smallAddToBasketCtaText = false,
  enableWishlistTabs,
  ProductTileComponent,
  enableImageGalleryAnimation,
  renderingPriority,
  bannerSlot,
}: {
  showConfirmOnDeleteItem?: boolean;
  onClickAddToBasket: (payload: WishlistAddToBasketPayload) => void;
  onClickSoldoutCta: (productId: number) => void;
  gridColumns?: number;
  smallAddToBasketCtaText?: boolean;
  enableWishlistTabs?: boolean;
  ProductTileComponent?: React.FC<
    Omit<ProductTileProps, 'ProductTileComponent'>
  >;
  enableImageGalleryAnimation?: boolean;
  bannerSlot?: React.ReactNode;
} & PriorityConfig) => {
  useActivatePreselectedBestCouponABTest();

  return (
    <WishlistStream
      gridColumns={gridColumns}
      onClickAddToBasket={onClickAddToBasket}
      onClickSoldoutCta={onClickSoldoutCta}
      showConfirmOnDeleteItem={showConfirmOnDeleteItem}
      smallAddToBasketCtaText={smallAddToBasketCtaText}
      enableWishlistTabs={enableWishlistTabs}
      ProductTileComponent={ProductTileComponent}
      enableImageGalleryAnimation={enableImageGalleryAnimation}
      renderingPriority={renderingPriority}
      bannerSlot={bannerSlot}
    />
  );
};
const WishlistItemStreamMemo = memo(WishlistItemStream);
export {WishlistItemStreamMemo as WishlistItemStream};

export interface WishlistStreamProps {
  showConfirmOnDeleteItem?: boolean;
  onClickAddToBasket: (payload: WishlistAddToBasketPayload) => void;
  onClickSoldoutCta: (productId: number) => void;
  gridColumns?: number;
  smallAddToBasketCtaText: boolean;
  enableWishlistTabs?: boolean;
  ProductTileComponent?: React.FC<
    Omit<ProductTileProps, 'ProductTileComponent'>
  >;
  enableImageGalleryAnimation?: boolean;
  /** Optional slot rendered between discount tabs and the product grid (mWeb only). */
  bannerSlot?: React.ReactNode;
}

const WishlistStream = (props: WishlistStreamProps & PriorityConfig) => {
  const {t} = useTranslation();

  const shallowRenderingState = useShallowRenderingState({
    uniqueKey: HISTORY_STATE_LAST_VISIBLE_ITEMS_KEY,
  });

  return (
    <PageDataFetcher
      errorHeadline={t('Oops! Something went wrong!')}
      props={props}
      shallowRenderingState={shallowRenderingState}
    />
  );
};

const PageDataFetcher = ({
  props,
  errorHeadline,
  shallowRenderingState,
}: {
  props: WishlistStreamProps & PriorityConfig;
  shallowRenderingState?: ShallowRenderingState;
  errorHeadline: string;
}) => {
  const {loadItems, deleteItem} = useWishlistPageStream();

  return (
    <DeleteItemProvider deleteItem={deleteItem}>
      <DataFetcher
        request={loadItems}
        Loading={WishlistLoadingComponent}
        Error={() => (
          <ErrorPosition>
            <ErrorPageInline headline={errorHeadline} />
          </ErrorPosition>
        )}
        View={
          props.enableWishlistTabs
            ? WishlistPageStreamViewMemo
            : WishlistStreamViewMemo
        }
        viewProps={{
          ...props,
          isDataFetching: loadItems.isFetching,
          isDataFetched: loadItems.isFetched,
          shallowRenderingState,
        }}
        renderingPriority={props.renderingPriority}
      />
    </DeleteItemProvider>
  );
};

const ErrorPosition = styled.div`
  grid-column: span 2;
  @media ${mediaDesktopAndUp} {
    grid-column: span 3;
  }

  @media ${mediaLargeScreens} {
    grid-column: span 4;
  }
`;
